import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Grid,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import { Search as SearchIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, CallMerge as CallMergeIcon } from '@mui/icons-material';
import { UniversalPagination } from '../../components/common/UniversalPagination';
import { StructuredWorkoutIcon } from '@components/icons'; // Updated import path to use index.ts
import {
  workoutService,
  WorkoutResponseDto,
  GetWorkoutsParams,
  WorkoutCategory,
  WorkoutSourceType,
  MergeWorkoutDto,
  Locale,
  MeasurementUnit, // Import MeasurementUnit enum
} from '../../services/workout.service';
import { useDebounce } from '../../hooks/useDebounce';
import { toastService } from '../../services/toast.service';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';

export const WorkoutsTab: React.FC = () => {
  const [workouts, setWorkouts] = useState<WorkoutResponseDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [limit, setLimit] = useState(10);

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const [categoryFilter, setCategoryFilter] = useState<WorkoutCategory | ''>('');
  const [sourceTypeFilter, setSourceTypeFilter] = useState<WorkoutSourceType | ''>('all');

  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [workoutToEdit, setWorkoutToEdit] = useState<WorkoutResponseDto | null>(null);

  const [isConfirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [workoutToDelete, setWorkoutToDelete] = useState<WorkoutResponseDto | null>(null);

  const [isMergeModalOpen, setMergeModalOpen] = useState(false);
  const [workoutToMerge, setWorkoutToMerge] = useState<WorkoutResponseDto | null>(null);
  const [mergeTarget, setMergeTarget] = useState<WorkoutResponseDto | null>(null);
  const [systemWorkouts, setSystemWorkouts] = useState<WorkoutResponseDto[]>([]);
  const [systemWorkoutsLoading, setSystemWorkoutsLoading] = useState(false);

  const locales: Locale[] = ['uk', 'en', 'ru', 'cs', 'es', 'pl', 'be'];

  // Ініціалізація currentWorkoutNameI18n
  const [currentWorkoutNameI18n, setCurrentWorkoutNameI18n] = useState<Partial<Record<Locale, string>>>(() => {
    const initialNames: Partial<Record<Locale, string>> = {};
    locales.forEach(loc => {
      initialNames[loc] = '';
    });
    return initialNames;
  });

  const [currentWorkoutCategory, setCurrentWorkoutCategory] = useState<WorkoutCategory>(WorkoutCategory.CARDIO);
  // const [currentMeasurementUnit, setCurrentMeasurementUnit] = useState<string>('одиниця'); // Localized string for display - REMOVED
  const [currentRawMeasurementUnit, setCurrentRawMeasurementUnit] = useState<MeasurementUnit>(MeasurementUnit.OTHER); // Enum value for API
  const [currentMetValue, setCurrentMetValue] = useState<number>(0);


  const fetchWorkouts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: GetWorkoutsParams = {
        page,
        limit,
        search: debouncedSearchQuery || undefined,
        sortBy: 'name',
        sortOrder: 'ASC',
        category: categoryFilter || undefined,
        sourceType: sourceTypeFilter || undefined,
      };
      const response = await workoutService.getWorkouts(params);
      setWorkouts(response.data);
      if (response.meta && response.meta.total && response.meta.limit) {
        setTotalPages(Math.ceil(response.meta.total / response.meta.limit));
      } else {
        setTotalPages(0);
      }
    } catch (err) {
      toastService.error('Не вдалося завантажити список тренувань.');
      setError('Не вдалося завантажити список тренувань.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearchQuery, categoryFilter, sourceTypeFilter]);

  useEffect(() => {
    fetchWorkouts();
  }, [fetchWorkouts]);

  const fetchSystemWorkouts = useCallback(async (search: string) => {
    setSystemWorkoutsLoading(true);
    try {
      const params: GetWorkoutsParams = {
        limit: 20,
        search: search || undefined,
        sourceType: 'system',
      };
      const response = await workoutService.getWorkouts(params);
      setSystemWorkouts(response.data);
    } catch (err) {
      toastService.error('Не вдалося завантажити системні тренування.');
    } finally {
      setSystemWorkoutsLoading(false);
    }
  }, []);

  const handlePageChange = (value: number) => setPage(value);
  const handleLimitChange = (value: number) => { setLimit(value); setPage(1); };
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };
  const handleCategoryFilterChange = useCallback((event: SelectChangeEvent<WorkoutCategory | ''>) => {
    setCategoryFilter(event.target.value as WorkoutCategory | '');
    setPage(1);
  }, []);
  const handleSourceTypeFilterChange = useCallback((event: SelectChangeEvent<WorkoutSourceType | ''>) => {
    setSourceTypeFilter(event.target.value as WorkoutSourceType | '');
    setPage(1);
  }, []);

  const handleOpenCreateModal = () => {
    const initialNames: Partial<Record<Locale, string>> = locales.reduce((acc, loc) => {
      acc[loc] = '';
      return acc;
    }, {} as Partial<Record<Locale, string>>);
    setCurrentWorkoutNameI18n(initialNames);
    setCurrentWorkoutCategory(WorkoutCategory.CARDIO);
    // setCurrentMeasurementUnit('одиниця'); // Localized default - REMOVED
    setCurrentRawMeasurementUnit(MeasurementUnit.OTHER); // Enum default
    setCurrentMetValue(0);
    setCreateModalOpen(true);
  };

  const prepareNameI18n = (nameI18n: { [key in Locale]?: string }) => {
    const cleanedNameI18n: Partial<Record<Locale, string>> = Object.entries(nameI18n)
      .filter(([, value]) => value && value.trim() !== '')
      .reduce((acc, [key, value]) => ({ ...acc, [key as Locale]: value }), {});

    if (!cleanedNameI18n.uk) {
      toastService.error('Назва тренування (uk) є обов\'язковою.');
      return null;
    }
    return cleanedNameI18n as Partial<Record<Locale, string>> & { uk: string };
  };

  const handleCreateWorkout = async () => {
    const nameI18n = prepareNameI18n(currentWorkoutNameI18n);
    if (!nameI18n) return;

    setLoading(true);
    try {
      await workoutService.createWorkout({
        nameI18n,
        category: currentWorkoutCategory,
        measurementUnit: currentRawMeasurementUnit, // Use enum value
        metValue: currentMetValue, // Use number
        defaultMeasurementValue: null, // Added defaultMeasurementValue
      });
      toastService.success('Тренування успішно створено!');
      setCreateModalOpen(false);
      fetchWorkouts();
    } catch (err: any) {
      toastService.error(`Помилка створення тренування: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = async (workout: WorkoutResponseDto) => {
    setLoading(true);
    setWorkoutToEdit(workout);
    try {
      const editData = await workoutService.getWorkoutForEdit(workout.id);
      setCurrentWorkoutNameI18n(editData.nameI18n as Partial<Record<Locale, string>>);
      setCurrentWorkoutCategory(editData.category);
      // setCurrentMeasurementUnit(editData.measurementUnit || 'одиниця'); // Localized string - REMOVED
      setCurrentRawMeasurementUnit(editData.rawMeasurementUnit as MeasurementUnit || MeasurementUnit.OTHER); // Enum value
      setCurrentMetValue(editData.metValue || 0);
      setEditModalOpen(true);
    } catch (err) {
      toastService.error('Не вдалося завантажити дані для редагування.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateWorkout = async () => {
    if (!workoutToEdit) return;
    
    const nameI18n = prepareNameI18n(currentWorkoutNameI18n);
    if (!nameI18n) return;

    setLoading(true);
    try {
      await workoutService.updateWorkout(workoutToEdit.id, {
        nameI18n,
        category: currentWorkoutCategory,
        measurementUnit: currentRawMeasurementUnit, // Use enum value
        metValue: currentMetValue, // Use number
      });
      toastService.success('Тренування успішно оновлено!');
      setEditModalOpen(false);
      setWorkoutToEdit(null);
      fetchWorkouts();
    } catch (err: any) {
      toastService.error(`Помилка оновлення тренування: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteConfirm = (workout: WorkoutResponseDto) => {
    setWorkoutToDelete(workout);
    setConfirmDeleteOpen(true);
  };

  const handleDeleteWorkout = async () => {
    if (!workoutToDelete) return;
    setLoading(true);
    try {
      await workoutService.deleteWorkout(workoutToDelete.id);
      toastService.success(`Тренування "${workoutToDelete.name}" успішно видалено.`);
      setConfirmDeleteOpen(false);
      setWorkoutToDelete(null);
      fetchWorkouts();
    } catch (err: any) {
      toastService.error(`Помилка видалення тренування: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMergeModal = (workout: WorkoutResponseDto) => {
    setWorkoutToMerge(workout);
    setMergeTarget(null);
    setSystemWorkouts([]);
    setMergeModalOpen(true);
  };

  const handleMergeWorkout = async () => {
    if (!workoutToMerge || !mergeTarget) return;
    setLoading(true);
    try {
      const mergeDto: MergeWorkoutDto = {
        sourceWorkoutId: workoutToMerge.id,
        targetWorkoutId: mergeTarget.id,
      };
      const result = await workoutService.mergeWorkouts(mergeDto);
      toastService.success(`Тренування '${result.removedWorkout.name}' об'єднано з '${result.targetWorkout.name}'. Оновлено подій: ${result.updatedCalendarEvents}.`);
      setMergeModalOpen(false);
      fetchWorkouts();
    } catch (err: any) {
      toastService.error(`Помилка злиття: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  const workoutCategories: WorkoutCategory[] = [WorkoutCategory.CARDIO, WorkoutCategory.STRENGTH, WorkoutCategory.FLEXIBILITY, WorkoutCategory.SPORTS, WorkoutCategory.OTHER];
  const workoutSourceTypes: WorkoutSourceType[] = ['all', 'system', 'user'];
  const workoutCategoryTranslations: { [key in WorkoutCategory]: string } = {
    [WorkoutCategory.CARDIO]: 'Кардіо',
    [WorkoutCategory.STRENGTH]: 'Силові',
    [WorkoutCategory.FLEXIBILITY]: 'Гнучкість',
    [WorkoutCategory.SPORTS]: 'Спорт',
    [WorkoutCategory.OTHER]: 'Інше',
  };

  const measurementUnitTranslations: { [key in MeasurementUnit]: string } = {
    [MeasurementUnit.MINUTE]: 'Хвилина',
    [MeasurementUnit.REPETITION]: 'Повторення',
    [MeasurementUnit.KILOMETER]: 'Кілометр',
    [MeasurementUnit.SET]: 'Підхід',
    [MeasurementUnit.STEP]: 'Крок',
    [MeasurementUnit.CALORIE]: 'Калорія',
    [MeasurementUnit.LEVEL]: 'Рівень',
    [MeasurementUnit.OTHER]: 'Інше',
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateModal}>Додати тренування</Button>
      </Box>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
          <Grid item xs={12} sm={6} md={4}><TextField fullWidth label="Пошук за назвою" value={searchQuery} onChange={handleSearchChange} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }} /></Grid>
          <Grid item xs={12} sm={6} md={3}><FormControl fullWidth><InputLabel>Категорія</InputLabel><Select value={categoryFilter} label="Категорія" onChange={handleCategoryFilterChange}><MenuItem value=""><em>Всі</em></MenuItem>{workoutCategories.map(c => <MenuItem key={c} value={c}>{workoutCategoryTranslations[c]}</MenuItem>)}</Select></FormControl></Grid>
          <Grid item xs={12} sm={6} md={3}><FormControl fullWidth><InputLabel>Джерело</InputLabel><Select value={sourceTypeFilter} label="Джерело" onChange={handleSourceTypeFilterChange}>{workoutSourceTypes.map(t => <MenuItem key={t} value={t}>{t === 'all' ? 'Всі' : t === 'system' ? 'Система' : 'Користувач'}</MenuItem>)}</Select></FormControl></Grid>
        </Grid>

        {error ? <Typography color="error" textAlign="center" sx={{ mt: 4 }}>{error}</Typography> : loading ? <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress /></Box> : workouts.length === 0 ? <Typography sx={{ mt: 4, textAlign: 'center' }}>Тренувань не знайдено.</Typography> : (
          <>
            <TableContainer><Table stickyHeader><TableHead><TableRow><TableCell>Назва</TableCell><TableCell>Категорія</TableCell><TableCell>Одиниця виміру</TableCell><TableCell>MET</TableCell><TableCell>Структуроване</TableCell><TableCell>Джерело</TableCell><TableCell align="right">Дії</TableCell></TableRow></TableHead><TableBody>{workouts.map((workout) => (<TableRow hover key={workout.id}><TableCell>{workout.name}</TableCell><TableCell>{workout.category}</TableCell><TableCell>{workout.measurementUnit}</TableCell><TableCell>{workout.metValue ?? '-'}</TableCell><TableCell>{workout.isStructured ? <StructuredWorkoutIcon /> : null}</TableCell><TableCell>{workout.createdByUserId ? 'Користувач' : 'Система'}</TableCell><TableCell align="right">{workout.createdByUserId && <IconButton onClick={() => handleOpenMergeModal(workout)} disabled={loading}><CallMergeIcon /></IconButton>}<IconButton onClick={() => handleOpenEditModal(workout)} disabled={loading}><EditIcon /></IconButton><IconButton onClick={() => handleOpenDeleteConfirm(workout)} disabled={loading}><DeleteIcon /></IconButton></TableCell></TableRow>))}</TableBody></Table></TableContainer>
            <UniversalPagination page={page} count={totalPages} limit={limit} onPageChange={handlePageChange} onLimitChange={handleLimitChange} storageKey="pagination:library-workouts" />
          </>
        )}
      </Paper>

      <Dialog open={isCreateModalOpen} onClose={() => setCreateModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Додати нове тренування</DialogTitle>
        <DialogContent><Box sx={{ pt: 1 }}>{locales.map(lang => <TextField key={lang} margin="dense" label={`Назва тренування (${lang.toUpperCase()})`} type="text" fullWidth variant="outlined" value={currentWorkoutNameI18n[lang] || ''} onChange={e => setCurrentWorkoutNameI18n({ ...currentWorkoutNameI18n, [lang]: e.target.value })} required={lang === 'uk'} />)}<FormControl fullWidth margin="dense"><InputLabel>Категорія</InputLabel><Select value={currentWorkoutCategory} label="Категорія" onChange={e => setCurrentWorkoutCategory(e.target.value as WorkoutCategory)}>{workoutCategories.map(c => <MenuItem key={c} value={c}>{workoutCategoryTranslations[c]}</MenuItem>)}</Select></FormControl>
        <FormControl fullWidth margin="dense">
            <InputLabel>Одиниця виміру</InputLabel>
            <Select
              value={currentRawMeasurementUnit}
              label="Одиниця виміру"
              onChange={e => setCurrentRawMeasurementUnit(e.target.value as MeasurementUnit)}
            >
              {Object.values(MeasurementUnit).map(unit => (
                <MenuItem key={unit} value={unit}>{measurementUnitTranslations[unit]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField margin="dense" label="MET значення" type="number" fullWidth variant="outlined" value={currentMetValue} onChange={e => setCurrentMetValue(parseFloat(e.target.value) || 0)} />
        </Box></DialogContent>
        <DialogActions><Button onClick={() => setCreateModalOpen(false)}>Скасувати</Button><Button onClick={handleCreateWorkout} disabled={loading} variant="contained">{loading ? <CircularProgress size={24} /> : 'Створити'}</Button></DialogActions>
      </Dialog>

      <Dialog open={isEditModalOpen} onClose={() => setEditModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Редагувати тренування</DialogTitle>
        <DialogContent><Box sx={{ pt: 1 }}>{locales.map(lang => <TextField key={lang} margin="dense" label={`Назва тренування (${lang.toUpperCase()})`} type="text" fullWidth variant="outlined" value={currentWorkoutNameI18n[lang] || ''} onChange={e => setCurrentWorkoutNameI18n({ ...currentWorkoutNameI18n, [lang]: e.target.value })} required={lang === 'uk'} />)}<FormControl fullWidth margin="dense"><InputLabel>Категорія</InputLabel><Select value={currentWorkoutCategory} label="Категорія" onChange={e => setCurrentWorkoutCategory(e.target.value as WorkoutCategory)}>{workoutCategories.map(c => <MenuItem key={c} value={c}>{workoutCategoryTranslations[c]}</MenuItem>)}</Select></FormControl>
        <FormControl fullWidth margin="dense">
            <InputLabel>Одиниця виміру</InputLabel>
            <Select
              value={currentRawMeasurementUnit}
              label="Одиниця виміру"
              onChange={e => setCurrentRawMeasurementUnit(e.target.value as MeasurementUnit)}
            >
              {Object.values(MeasurementUnit).map(unit => (
                <MenuItem key={unit} value={unit}>{measurementUnitTranslations[unit]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField margin="dense" label="MET значення" type="number" fullWidth variant="outlined" value={currentMetValue} onChange={e => setCurrentMetValue(parseFloat(e.target.value) || 0)} />
        </Box></DialogContent>
        <DialogActions><Button onClick={() => setEditModalOpen(false)}>Скасувати</Button><Button onClick={handleUpdateWorkout} disabled={loading} variant="contained">{loading ? <CircularProgress size={24} /> : 'Зберегти зміни'}</Button></DialogActions>
      </Dialog>

      <ConfirmationDialog open={isConfirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} onConfirm={handleDeleteWorkout} title="Підтвердити видалення" message={`Ви впевнені, що хочете видалити тренування "${workoutToDelete?.name}"?`} confirmText="Видалити" />

      <Dialog open={isMergeModalOpen} onClose={() => setMergeModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Об'єднати тренування</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Ви збираєтеся об'єднати тренування <strong>{workoutToMerge?.name}</strong> з системним тренуванням. Усі записи в календарі будуть оновлені, а це тренування буде видалено.</Typography>
          <Autocomplete
            options={systemWorkouts}
            getOptionLabel={(option) => `${option.name} (${workoutCategoryTranslations[option.category as WorkoutCategory] || option.category})`}
            loading={systemWorkoutsLoading}
            value={mergeTarget}
            onChange={(_event, newValue) => setMergeTarget(newValue)}
            onInputChange={(_event, newInputValue) => fetchSystemWorkouts(newInputValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Пошук системного тренування"
                variant="outlined"
                InputProps={{ ...params.InputProps, endAdornment: <>{systemWorkoutsLoading ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</> }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeModalOpen(false)}>Скасувати</Button>
          <Button onClick={handleMergeWorkout} disabled={!mergeTarget || loading} variant="contained">{loading ? <CircularProgress size={24} /> : "Об'єднати"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};